import {
  BOUNDARY_VERTEX_HIT_PIXELS,
  findNearestVertex,
  toLngLatRing,
} from "./boundaryUtils";
import {
  pointAlongSegment,
  type BoundaryPoint,
  type MapViewportProjector,
  type ScreenPoint,
} from "./geo";
import {
  boundaryRing,
  collectUniqueEdges,
  rowLongSideCenterline,
  toRadians,
} from "./gridMeshGeometry";
import {
  collectRowDividerEdges,
  collectRowLabelFeatures,
  toRowLabelFeature,
} from "./gridRowSplit";
import type { GridCellSeed, GridMesh } from "./gridMeshTypes";

/** Default lattice row count for a new plot grid. */
export const DEFAULT_GRID_ROWS = 4;

/** Default lattice column count for a new plot grid. */
export const DEFAULT_GRID_COLUMNS = 25;

/** Key for a lattice `GridCellSeed` at `row` / `column` (row 0 is the northern band). */
export const gridCellSeedKey = (row: number, column: number): string =>
  `r${row}-c${column}`;

/** Default east–west plot length (meters) before commit. */
export const DEFAULT_PLOT_LENGTH_METERS = 6;

/** Default north–south plot width (meters) before commit. */
export const DEFAULT_PLOT_WIDTH_METERS = 3;

/** WGS84 authalic radius (m); matches boundaryUtils spherical helpers. */
const EARTH_RADIUS_METERS = 6_378_137;

/** Approximate meters per degree of latitude (constant on a sphere). */
const METERS_PER_DEGREE_LATITUDE = (EARTH_RADIUS_METERS * Math.PI) / 180;

/** Minimum half-extent (degrees) so the grid cannot collapse. */
export const MIN_GRID_HALF_EXTENT = 1e-7;

/** Plot grid line/fill color and trial title labels. */
export const PLOT_GRID_CYAN = "#00e5ff";

/** Screen-space gap between the grid top edge and the trial title label. */
export const GRID_TRIAL_LABEL_PIXEL_OFFSET = 18;

export interface GridTransform {
  centerLatitude: number;
  centerLongitude: number;
  halfHeight: number;
  halfWidth: number;
  rotationDegrees: number;
}

export interface PlotGrid {
  /**
   * Plot metadata keyed by lattice position (`gridCellSeedKey`). Applied to
   * lattice labels and baked into the mesh on commit.
   */
  cellSeeds?: Record<string, GridCellSeed>;
  /**
   * Lattice column count for the pre-commit grid. Null when hydrated from
   * server plots (the saved shape might not bea regular lattice).
   */
  columns: number | null;
  /**
   * When true, column labels run east→west (…→01) instead of west→east
   * (01→…). Lattice-stage only; baked into cell labels on commit.
   */
  invertColumnLabels: boolean;
  /**
   * When true, row labels run north→south (…→A) instead of south→north
   * (A→…). Lattice-stage only; baked into cell labels on commit.
   */
  invertRowLabels: boolean;
  isCommitted: boolean;
  /** Present after commit; topology edits mutate this mesh. */
  mesh?: GridMesh;
  /**
   * East–west plot length in meters for the pre-commit lattice. Null when
   * hydrated from server plots.
   */
  plotLengthMeters: number | null;
  /**
   * North–south plot width in meters for the pre-commit lattice. Null when
   * hydrated from server plots.
   */
  plotWidthMeters: number | null;
  /** Present before commit (and kept empty after commit). */
  points: BoundaryPoint[];
  /**
   * Lattice row count for the pre-commit grid. Null when hydrated from
   * server plots (shape is not a regular lattice).
   */
  rows: number | null;
  transform: GridTransform;
}

export interface PlotGridSizeMeters {
  plotLengthMeters: number;
  plotWidthMeters: number;
}

export interface CreatePlotGridOptions extends Partial<PlotGridSizeMeters> {
  cellSeeds?: Record<string, GridCellSeed>;
  invertColumnLabels?: boolean;
  invertRowLabels?: boolean;
}

export type GridHandleRole = "corner";

export interface GridHandleHit {
  cornerIndex?: number;
  role: GridHandleRole;
}

interface LocalPoint {
  x: number;
  y: number;
}

/** Translate a geo point into coordinates relative to the transform center. */
const toLocal = (
  point: BoundaryPoint,
  transform: GridTransform,
): LocalPoint => ({
  x: point.longitude - transform.centerLongitude,
  y: point.latitude - transform.centerLatitude,
});

/** Translate local offsets back into absolute lng/lat. */
const fromLocal = (
  local: LocalPoint,
  transform: GridTransform,
): BoundaryPoint => ({
  latitude: transform.centerLatitude + local.y,
  longitude: transform.centerLongitude + local.x,
});

/** Rotate a local (x, y) by `rotationDegrees` (counter-clockwise). */
const rotateLocal = (
  local: LocalPoint,
  rotationDegrees: number,
): LocalPoint => {
  const radians = toRadians(rotationDegrees);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return {
    x: local.x * cos - local.y * sin,
    y: local.x * sin + local.y * cos,
  };
};

/** Inverse of rotateLocal — undo the grid's rotation. */
const unrotateLocal = (
  local: LocalPoint,
  rotationDegrees: number,
): LocalPoint => rotateLocal(local, -rotationDegrees);

/**
 * Build a transform whose overall footprint matches known per-plot size ×
 * rows/columns. `plotRowHeightMeters` is north–south (per range row);
 * `plotColumnWidthMeters` is east–west (per crop-row block column).
 */
export const createPlotSizedGridTransform = (
  center: BoundaryPoint,
  rows: number,
  columns: number,
  plotRowHeightMeters: number,
  plotColumnWidthMeters: number,
  rotationDegrees = 0,
): GridTransform => {
  const totalHeightMeters = plotRowHeightMeters * rows;
  const totalWidthMeters = plotColumnWidthMeters * columns;
  const halfHeight = Math.max(
    MIN_GRID_HALF_EXTENT,
    totalHeightMeters / 2 / METERS_PER_DEGREE_LATITUDE,
  );
  const halfWidth = Math.max(
    MIN_GRID_HALF_EXTENT,
    totalWidthMeters / 2 / METERS_PER_DEGREE_LATITUDE,
  );

  return {
    centerLatitude: center.latitude,
    centerLongitude: center.longitude,
    halfHeight,
    halfWidth,
    rotationDegrees,
  };
};

/**
 * Inverse of createPlotSizedGridTransform: per-plot length/width from the
 * current footprint and lattice dimensions. Rounded to mm for stable UI.
 */
export const plotSizeFromGridTransform = (
  transform: GridTransform,
  rows: number,
  columns: number,
): { plotLengthMeters: number; plotWidthMeters: number } => {
  const safeRows = Math.max(1, rows);
  const safeColumns = Math.max(1, columns);
  const totalHeightMeters =
    transform.halfHeight * 2 * METERS_PER_DEGREE_LATITUDE;
  const totalWidthMeters = transform.halfWidth * 2 * METERS_PER_DEGREE_LATITUDE;

  return {
    plotLengthMeters: Math.round((totalHeightMeters / safeRows) * 1000) / 1000,
    plotWidthMeters: Math.round((totalWidthMeters / safeColumns) * 1000) / 1000,
  };
};

export const getGridPointIndex = (
  row: number,
  column: number,
  columns: number,
): number => row * (columns + 1) + column;

/**
 * Build a (rows+1)×(columns+1) lattice in the transform's local frame
 * (u,v ∈ [-1,1]), rotate by rotationDegrees, then map to geo points.
 */
export const buildGridPoints = (
  rows: number,
  columns: number,
  transform: GridTransform,
): BoundaryPoint[] => {
  if (rows < 1 || columns < 1) {
    return [];
  }

  const points: BoundaryPoint[] = [];

  for (let row = 0; row <= rows; row += 1) {
    // v runs north→south in [-1, 1]; u west→east. Row 0 is the northern edge.
    const v = 1 - (row / rows) * 2;
    for (let column = 0; column <= columns; column += 1) {
      const u = (column / columns) * 2 - 1;
      // Scale by half-extents, rotate into world, then offset by center.
      const local = rotateLocal(
        {
          x: u * transform.halfWidth,
          y: v * transform.halfHeight,
        },
        transform.rotationDegrees,
      );
      points.push(fromLocal(local, transform));
    }
  }

  return points;
};

export const createPlotGrid = (
  rows: number,
  columns: number,
  transform: GridTransform,
  options?: CreatePlotGridOptions,
): PlotGrid => ({
  cellSeeds: options?.cellSeeds,
  columns,
  invertColumnLabels: options?.invertColumnLabels ?? false,
  invertRowLabels: options?.invertRowLabels ?? false,
  isCommitted: false,
  plotLengthMeters: options?.plotLengthMeters ?? DEFAULT_PLOT_LENGTH_METERS,
  plotWidthMeters: options?.plotWidthMeters ?? DEFAULT_PLOT_WIDTH_METERS,
  points: buildGridPoints(rows, columns, transform),
  rows,
  transform,
});

/** Corners in order: SW, SE, NE, NW (local unrotated signs). */
export const getGridCornerPoints = (
  transform: GridTransform,
): BoundaryPoint[] => {
  const localCorners: LocalPoint[] = [
    { x: -transform.halfWidth, y: -transform.halfHeight },
    { x: transform.halfWidth, y: -transform.halfHeight },
    { x: transform.halfWidth, y: transform.halfHeight },
    { x: -transform.halfWidth, y: transform.halfHeight },
  ];

  return localCorners.map((local) =>
    fromLocal(rotateLocal(local, transform.rotationDegrees), transform),
  );
};

/** Point at the grid top edge center for a trial title label anchor. */
export const getPlotGridTrialLabelPoint = (
  grid: PlotGrid,
): [number, number] | null => {
  const { transform } = grid;

  if (grid.isCommitted && grid.mesh != null) {
    const vertices = Object.values(grid.mesh.vertices);
    if (vertices.length === 0) {
      return null;
    }

    let maxLocalY = Number.NEGATIVE_INFINITY;
    let sumLocalX = 0;
    let topCount = 0;

    vertices.forEach((vertex) => {
      const local = toLocal(vertex, transform);
      if (local.y > maxLocalY + MIN_GRID_HALF_EXTENT) {
        maxLocalY = local.y;
        sumLocalX = local.x;
        topCount = 1;
      } else if (Math.abs(local.y - maxLocalY) <= MIN_GRID_HALF_EXTENT) {
        sumLocalX += local.x;
        topCount += 1;
      }
    });

    const centerX = sumLocalX / topCount;
    const point = fromLocal(
      rotateLocal({ x: centerX, y: maxLocalY }, transform.rotationDegrees),
      transform,
    );
    return [point.longitude, point.latitude];
  }

  const point = fromLocal(
    rotateLocal({ x: 0, y: transform.halfHeight }, transform.rotationDegrees),
    transform,
  );
  return [point.longitude, point.latitude];
};

/** Local-axis signs for corners in getGridCornerPoints order: SW, SE, NE, NW. */
const CORNER_LOCAL_SIGNS: LocalPoint[] = [
  { x: -1, y: -1 },
  { x: 1, y: -1 },
  { x: 1, y: 1 },
  { x: -1, y: 1 },
];

/**
 * Move one corner to `pointer`. The opposite corner stays put; the adjacent
 * corners slide so the grid remains a rectangle. Half-extents and center
 * update, and `buildGridPoints` then spaces the lattice evenly inside it.
 */
export const resizeGridTransformFromCorner = (
  transform: GridTransform,
  pointer: BoundaryPoint,
  cornerIndex: number,
): GridTransform => {
  const sign = CORNER_LOCAL_SIGNS[cornerIndex] ?? CORNER_LOCAL_SIGNS[0];
  if (sign == null) {
    return transform;
  }

  const minSpan = MIN_GRID_HALF_EXTENT * 2;
  const pointerLocal = unrotateLocal(
    toLocal(pointer, transform),
    transform.rotationDegrees,
  );
  const fixed = {
    x: -sign.x * transform.halfWidth,
    y: -sign.y * transform.halfHeight,
  };
  const spanX = Math.max(
    minSpan,
    sign.x < 0 ? fixed.x - pointerLocal.x : pointerLocal.x - fixed.x,
  );
  const spanY = Math.max(
    minSpan,
    sign.y < 0 ? fixed.y - pointerLocal.y : pointerLocal.y - fixed.y,
  );
  const draggedX = fixed.x + sign.x * spanX;
  const draggedY = fixed.y + sign.y * spanY;
  const centerWorld = fromLocal(
    rotateLocal(
      {
        x: (fixed.x + draggedX) / 2,
        y: (fixed.y + draggedY) / 2,
      },
      transform.rotationDegrees,
    ),
    transform,
  );

  return {
    ...transform,
    centerLatitude: centerWorld.latitude,
    centerLongitude: centerWorld.longitude,
    halfHeight: spanY / 2,
    halfWidth: spanX / 2,
  };
};

/**
 * Signed angle from geographic north to `pointer`, in degrees (CCW positive).
 * Matches the sign of `rotationDegrees`.
 */
export const pointerAngleFromNorthDegrees = (
  center: BoundaryPoint,
  pointer: BoundaryPoint,
): number => {
  const x = pointer.longitude - center.longitude;
  const y = pointer.latitude - center.latitude;
  return (-Math.atan2(x, y) * 180) / Math.PI;
};

/** Signed angle from local +Y to `pointer`, in degrees (CCW positive). */
export const gridPointerAngleDegrees = (
  transform: GridTransform,
  pointer: BoundaryPoint,
): number =>
  pointerAngleFromNorthDegrees(
    {
      latitude: transform.centerLatitude,
      longitude: transform.centerLongitude,
    },
    pointer,
  );

/** Wrap a degree delta into (-180, 180] so a south-seam crossing stays small. */
export const normalizeDegreesDelta = (degrees: number): number => {
  const wrapped = ((degrees % 360) + 360) % 360;
  return wrapped > 180 ? wrapped - 360 : wrapped;
};

/**
 * Set rotation so local +Y points toward `pointer`
 * (atan2 of the center→pointer vector, converted to degrees).
 */
export const rotateGridTransformToPoint = (
  transform: GridTransform,
  pointer: BoundaryPoint,
): GridTransform => ({
  ...transform,
  rotationDegrees: gridPointerAngleDegrees(transform, pointer),
});

/**
 * Rotate around the center by the pointer's angular movement since
 * `previousPointerAngleDegrees`. `rotationDegrees` is the drag's accumulated
 * rotation (not necessarily `transform.rotationDegrees`, which can be stale
 * between pointer events).
 */
export const rotateGridTransformByPointerDelta = (
  transform: GridTransform,
  rotationDegrees: number,
  previousPointerAngleDegrees: number,
  pointer: BoundaryPoint,
): { pointerAngleDegrees: number; transform: GridTransform } => {
  const pointerAngleDegrees = gridPointerAngleDegrees(transform, pointer);

  return {
    pointerAngleDegrees,
    transform: {
      ...transform,
      rotationDegrees:
        rotationDegrees +
        normalizeDegreesDelta(
          pointerAngleDegrees - previousPointerAngleDegrees,
        ),
    },
  };
};

/** True when `point` lies inside the rotated grid rectangle (inclusive edges). */
export const isPointInGrid = (
  point: BoundaryPoint,
  transform: GridTransform,
): boolean => {
  // Unrotate into the axis-aligned local frame, then AABB-test.
  const unrotated = unrotateLocal(
    toLocal(point, transform),
    transform.rotationDegrees,
  );

  return (
    Math.abs(unrotated.x) <= transform.halfWidth &&
    Math.abs(unrotated.y) <= transform.halfHeight
  );
};

export const moveGridTransform = (
  transform: GridTransform,
  center: BoundaryPoint,
): GridTransform => ({
  ...transform,
  centerLatitude: center.latitude,
  centerLongitude: center.longitude,
});

/** Hit-test corner handles in screen space. */
export const findGridHandleAtScreenPoint = (
  map: MapViewportProjector,
  transform: GridTransform,
  screenPoint: ScreenPoint,
  hitPixels: number = BOUNDARY_VERTEX_HIT_PIXELS,
): GridHandleHit | null => {
  const cornerPoints = getGridCornerPoints(transform);
  const nearestCorner = findNearestVertex(map, cornerPoints, screenPoint);
  if (nearestCorner != null && nearestCorner.distancePx <= hitPixels) {
    return {
      cornerIndex: nearestCorner.index,
      role: "corner",
    };
  }

  return null;
};

/**
 * Long-axis centerline for a plot label. Mapbox places the label along this
 * line the same way grower and site names follow their boundary.
 */
const plotLabelLine = (
  ring: readonly BoundaryPoint[],
): Array<[number, number]> | null => {
  const centerline = rowLongSideCenterline(ring);
  if (centerline == null) {
    return null;
  }

  const [start, end] = centerline;
  return [
    [start.longitude, start.latitude],
    [end.longitude, end.latitude],
  ];
};

/** Average of ring vertices (lng/lat); used for cell label placement. */
const ringCentroid = (
  ring: Array<[number, number]>,
): [number, number] | null => {
  if (ring.length === 0) {
    return null;
  }

  let sumLng = 0;
  let sumLat = 0;
  ring.forEach(([lng, lat]) => {
    sumLng += lng;
    sumLat += lat;
  });

  return [sumLng / ring.length, sumLat / ring.length];
};

const addGridTrialLabelFeature = (
  features: Array<{
    type: "Feature";
    geometry:
      | {
          type: "LineString";
          coordinates: Array<[number, number]>;
        }
      | {
          type: "Point";
          coordinates: [number, number];
        }
      | {
          type: "Polygon";
          coordinates: Array<Array<[number, number]>>;
        };
    properties: {
      cellId?: string;
      cornerIndex?: number;
      index?: number;
      kind:
        | "grid-cell"
        | "grid-handle"
        | "grid-label"
        | "grid-line"
        | "grid-row-label"
        | "grid-row-line"
        | "grid-trial-label"
        | "grid-vertex";
      label?: string;
      plotGeoId?: number;
      role?: GridHandleRole | "row" | "vertex";
      rotation?: number;
      selected?: boolean;
    };
  }>,
  grid: PlotGrid,
  trialLabel: string | null | undefined,
): void => {
  const trimmedLabel = trialLabel?.trim() ?? "";
  if (trimmedLabel === "") {
    return;
  }

  const coordinates = getPlotGridTrialLabelPoint(grid);
  if (coordinates == null) {
    return;
  }

  features.push({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates,
    },
    properties: {
      kind: "grid-trial-label",
      label: trimmedLabel,
    },
  });
};

/**
 * Crop rows inside a lattice cell, west→east from `minRow` toward `maxRow`.
 * Commit splits a plot on this same span.
 */
const latticeCropRowSpan = (
  seed: GridCellSeed | undefined,
): { minRow: number; rowCount: number } | null => {
  if (seed?.plotGeoId == null || seed.minRow == null || seed.maxRow == null) {
    return null;
  }

  const rowCount = seed.maxRow - seed.minRow + 1;
  if (!Number.isInteger(rowCount) || rowCount < 1) {
    return null;
  }

  return { minRow: seed.minRow, rowCount };
};

/**
 * Even row dividers across a lattice cell, west→east. Endpoints match the
 * committed mesh: north edge (vertex 0→1) to south edge (vertex 3→2).
 */
const latticeRowDividerLines = (
  seed: GridCellSeed | undefined,
  northWest: BoundaryPoint,
  northEast: BoundaryPoint,
  southWest: BoundaryPoint,
  southEast: BoundaryPoint,
): Array<[[number, number], [number, number]]> => {
  const span = latticeCropRowSpan(seed);
  if (span == null || span.rowCount < 2) {
    return [];
  }

  const lines: Array<[[number, number], [number, number]]> = [];
  for (let index = 1; index < span.rowCount; index += 1) {
    const t = index / span.rowCount;
    const north = pointAlongSegment(northWest, northEast, t);
    const south = pointAlongSegment(southWest, southEast, t);
    lines.push([
      [north.longitude, north.latitude],
      [south.longitude, south.latitude],
    ]);
  }

  return lines;
};

/**
 * `range/row` labels for each lattice crop row. Strip order matches commit:
 * NW → NE → SE → SW, so the long-side rotation matches the mesh labels.
 */
const latticeRowLabelFeatures = (
  seed: GridCellSeed | undefined,
  range: number,
  northWest: BoundaryPoint,
  northEast: BoundaryPoint,
  southWest: BoundaryPoint,
  southEast: BoundaryPoint,
) => {
  const span = latticeCropRowSpan(seed);
  if (span == null) {
    return [];
  }

  const features = [];
  for (let index = 0; index < span.rowCount; index += 1) {
    const startT = index / span.rowCount;
    const endT = (index + 1) / span.rowCount;
    const feature = toRowLabelFeature(
      [
        pointAlongSegment(northWest, northEast, startT),
        pointAlongSegment(northWest, northEast, endT),
        pointAlongSegment(southWest, southEast, endT),
        pointAlongSegment(southWest, southEast, startT),
      ],
      range,
      span.minRow + index,
      "grid-row-label",
    );
    if (feature != null) {
      features.push(feature);
    }
  }

  return features;
};

export const toGridGeoJson = (
  grid: PlotGrid,
  options?: {
    selectedCellIds?: string[];
    selectedVertexId?: string | null;
    trialLabel?: string | null;
  },
) => {
  const selectedCellIds = new Set(options?.selectedCellIds ?? []);
  const selectedVertexId = options?.selectedVertexId ?? null;
  const trialLabel = options?.trialLabel ?? null;

  const features: Array<{
    type: "Feature";
    geometry:
      | {
          type: "LineString";
          coordinates: Array<[number, number]>;
        }
      | {
          type: "Point";
          coordinates: [number, number];
        }
      | {
          type: "Polygon";
          coordinates: Array<Array<[number, number]>>;
        };
    properties: {
      cellId?: string;
      cornerIndex?: number;
      index?: number;
      kind:
        | "grid-cell"
        | "grid-handle"
        | "grid-label"
        | "grid-line"
        | "grid-row-label"
        | "grid-row-line"
        | "grid-trial-label"
        | "grid-vertex";
      label?: string;
      plotGeoId?: number;
      role?: GridHandleRole | "row" | "vertex";
      rotation?: number;
      selected?: boolean;
    };
  }> = [];

  if (grid.isCommitted && grid.mesh != null) {
    const { cells, vertices } = grid.mesh;

    cells.forEach((cell) => {
      const ringPoints = boundaryRing(cell.vertexIds, vertices);
      const ring = toLngLatRing(ringPoints);
      if (ring.length < 3) {
        return;
      }

      features.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [[...ring, ring[0]]],
        },
        properties: {
          cellId: cell.id,
          kind: "grid-cell",
          plotGeoId: cell.plotGeoId,
          selected: selectedCellIds.has(cell.id),
        },
      });

      const centroid = ringCentroid(ring);
      const labelLine = plotLabelLine(ringPoints);
      const cellLabel = cell.label.trim();
      if (centroid != null && labelLine != null && cellLabel !== "") {
        features.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: labelLine,
          },
          properties: {
            cellId: cell.id,
            kind: "grid-label",
            label: cellLabel,
          },
        });
      }
    });

    collectUniqueEdges(cells).forEach((edge) => {
      const start = vertices[edge.startVertexId];
      const end = vertices[edge.endVertexId];
      if (start == null || end == null) {
        return;
      }

      features.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [
            [start.longitude, start.latitude],
            [end.longitude, end.latitude],
          ],
        },
        properties: {
          kind: "grid-line",
        },
      });
    });

    collectRowDividerEdges(grid.mesh).forEach((edge) => {
      const start = vertices[edge.startVertexId];
      const end = vertices[edge.endVertexId];
      if (start == null || end == null) {
        return;
      }

      features.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [
            [start.longitude, start.latitude],
            [end.longitude, end.latitude],
          ],
        },
        properties: {
          kind: "grid-row-line",
        },
      });
    });

    collectRowLabelFeatures(grid.mesh, "grid-row-label").forEach((feature) => {
      features.push(feature);
    });

    Object.values(vertices).forEach((vertex) => {
      features.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [vertex.longitude, vertex.latitude],
        },
        properties: {
          index: undefined,
          kind: "grid-vertex",
          role: vertex.onEdge == null ? "vertex" : "row",
          selected: selectedVertexId != null && vertex.id === selectedVertexId,
        },
      });
    });

    addGridTrialLabelFeature(features, grid, trialLabel);

    return {
      type: "FeatureCollection" as const,
      features,
    };
  }

  const { columns, points, rows } = grid;
  if (rows == null || columns == null) {
    return {
      type: "FeatureCollection" as const,
      features,
    };
  }

  const columnCount = columns + 1;

  for (let row = 0; row <= rows; row += 1) {
    const coordinates: Array<[number, number]> = [];
    for (let column = 0; column <= columns; column += 1) {
      const point = points[getGridPointIndex(row, column, columns)];
      if (point == null) {
        continue;
      }
      coordinates.push([point.longitude, point.latitude]);
    }
    if (coordinates.length >= 2) {
      features.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates,
        },
        properties: {
          kind: "grid-line",
        },
      });
    }
  }

  for (let column = 0; column <= columns; column += 1) {
    const coordinates: Array<[number, number]> = [];
    for (let row = 0; row <= rows; row += 1) {
      const point = points[row * columnCount + column];
      if (point == null) {
        continue;
      }
      coordinates.push([point.longitude, point.latitude]);
    }
    if (coordinates.length >= 2) {
      features.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates,
        },
        properties: {
          kind: "grid-line",
        },
      });
    }
  }

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      // Row 0 is the northern band. Order matches commit: NW → NE → SE → SW.
      const northWest = points[getGridPointIndex(row, column, columns)];
      const northEast = points[getGridPointIndex(row, column + 1, columns)];
      const southEast = points[getGridPointIndex(row + 1, column + 1, columns)];
      const southWest = points[getGridPointIndex(row + 1, column, columns)];
      if (
        northWest == null ||
        northEast == null ||
        southEast == null ||
        southWest == null
      ) {
        continue;
      }

      const seed = grid.cellSeeds?.[gridCellSeedKey(row, column)];
      const range = seed?.range ?? rows - row;
      latticeRowDividerLines(
        seed,
        northWest,
        northEast,
        southWest,
        southEast,
      ).forEach((coordinates) => {
        features.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates,
          },
          properties: {
            kind: "grid-row-line",
          },
        });
      });
      latticeRowLabelFeatures(
        seed,
        range,
        northWest,
        northEast,
        southWest,
        southEast,
      ).forEach((feature) => {
        features.push(feature);
      });

      const centroid = ringCentroid([
        [northWest.longitude, northWest.latitude],
        [northEast.longitude, northEast.latitude],
        [southEast.longitude, southEast.latitude],
        [southWest.longitude, southWest.latitude],
      ]);
      if (centroid == null) {
        continue;
      }

      const seedLabel = seed?.label?.trim() ?? "";
      const labelLine = plotLabelLine([
        northWest,
        northEast,
        southEast,
        southWest,
      ]);
      if (seedLabel === "" || labelLine == null) {
        continue;
      }

      features.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: labelLine,
        },
        properties: {
          cellId: `gc-r${row}-c${column}`,
          kind: "grid-label",
          label: seedLabel,
        },
      });
    }
  }

  getGridCornerPoints(grid.transform).forEach((point, cornerIndex) => {
    features.push({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [point.longitude, point.latitude],
      },
      properties: {
        cornerIndex,
        kind: "grid-handle",
        role: "corner",
      },
    });
  });

  addGridTrialLabelFeature(features, grid, trialLabel);

  return {
    type: "FeatureCollection" as const,
    features,
  };
};
